/**
 * Cascade Forms and Form Submissions — Supabase-backed.
 *
 * Composed entirely from shared pieces: the record workspace from
 * @command-center/ui-system (card/table toggle, split view, condensed list,
 * detail shell) and the submission card/detail from
 * @command-center/oneup-portal-ui. Nothing here is Cascade-specific except the
 * API client, so another portal gets this surface by pointing the same
 * components at its own endpoint.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, FileText, Inbox, Plus, Trash2 } from 'lucide-react';
import {
  CompactRecordList,
  CompactRecordListItem,
  DateRangeFilter,
  FilterSelect,
  ListTabs,
  ListToolbar,
  Pagination,
  RecordCardGrid,
  RecordDetailShell,
  RecordTable,
  RecordTableCell,
  RecordTablePrimaryCell,
  RecordTableRow,
  RecordTypeStrip,
  SearchField,
  WorkspaceSplitView,
  dateRangeFromPreset,
  useCollectionView,
  useIsSplitView,
  usePageHeader,
  Button,
} from '@command-center/ui-system';
import {
  FormClassificationPill,
  FormSubmissionCard,
  FormSubmissionDetail,
  FormSubmissionListItem,
  RecordActivity,
  SharedRecordManagement,
  RecordPill,
  type RecordAnalysisEntry,
} from '@command-center/oneup-portal-ui';
import { Badge, CollectionViewToggle } from '@command-center/ui-system';
import { formsApi, type CascadeForm, type CascadeSubmission, type FormTriageView } from '../lib/forms-api';
import { usePagedView } from '../lib/use-paged-view';
import {
  IntakeLeadHeaderAction,
  IntakeRecordStatusBanner,
  useIntakeSourceState,
} from '../components/intake-action-banner';
import { IntakeActionCenter } from '../components/intake-action-center';
import { LinkedContactRecords } from '../components/linked-contact-records';
import { EmptyState, ErrorState, IntakeStateBadge, LoadingState } from './parts';
import { leadloopApi } from '../lib/leadloop-api';
import { BulkActionBar, SelectionCheckbox, useBulkSelection } from '../components/bulk-record-actions';

function formatDateTime(value?: string): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/* ------------------------------------------------------------------ submissions */

export function FormSubmissionsView() {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [recordView, setRecordView] = useState('default');
  const [dates, setDates] = useState('all');
  const [triage, setTriage] = useState<FormTriageView>('actionable');
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useCollectionView('cascade:records:form-submissions:view', 'list');

  const { page, setPage, pageSize } = usePagedView([recordView, query, status, triage, dates]);
  const submissions = useQuery({
    queryKey: ['cascade-form-submissions', recordView, query, status, triage, dates, page, pageSize],
    queryFn: () => formsApi.submissions({
      q: query || undefined,
      reviewStatus: status,
      triage,
      view: recordView,
      dateFrom: dateRangeFromPreset(dates).from,
      page,
      pageSize,
    }),
    placeholderData: (previous) => previous,
  });

  const items = submissions.data?.items ?? [];
  const actionableIds = items
    .filter((item) => !item.archived && (item.lifecycle === 'new' || item.lifecycle === 'open'))
    .map((item) => item.id);
  const selection = useBulkSelection(actionableIds);
  const pagination = (
    <Pagination
      page={page}
      pageSize={pageSize}
      total={submissions.data?.total}
      count={items.length}
      onPageChange={setPage}
      busy={submissions.isFetching}
      label="form entries"
    />
  );
  const selectedId = searchParams.get('entry');
  const listedSelected = items.find((entry) => entry.id === selectedId) ?? null;
  const selectedDetail = useQuery({
    queryKey: ['cascade-form-submission-detail', selectedId],
    queryFn: () => formsApi.submission(selectedId!),
    enabled: Boolean(selectedId),
  });
  const selected = selectedDetail.data ?? listedSelected;
  const selectedIntakeState = useIntakeSourceState(
    'form_submission',
    selectedId ?? undefined,
    selected?.airtableLeadRecordId,
  );
  const queryClient = useQueryClient();
  const bulk = useMutation({
    mutationFn: (action: 'create_leads' | 'dismiss') => leadloopApi.bulkIntake(
      selection.selectedIds.map((sourceId) => ({ sourceType: 'form_submission', sourceId })),
      action,
      action === 'dismiss' ? { disposition: 'not_qualified' } : {},
    ),
    onSuccess: () => {
      selection.clear();
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['cascade-form-submissions'] }),
        queryClient.invalidateQueries({ queryKey: ['intake'] }),
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
        queryClient.invalidateQueries({ queryKey: ['loop'] }),
      ]);
    },
  });
  const selectedItem = selected;
  const refreshSelected = async () => {
    if (!selected) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['cascade-form-submission-detail', selected.id] }),
      queryClient.invalidateQueries({ queryKey: ['cascade-form-submissions'] }),
      queryClient.invalidateQueries({ queryKey: ['cascade', 'leadloop'] }),
    ]);
  };
  const setSelected = (entry: CascadeSubmission | null) => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      if (entry) params.set('entry', entry.id);
      else params.delete('entry');
      return params;
    });
  };
  const index = selected ? items.findIndex((item) => item.id === selected.id) : -1;
  const total = submissions.data?.total ?? items.length;
  /*
    Below the split breakpoint the detail REPLACES the list, so the tabs, the
    search box, and the filters above it are acting on something nobody can see.
    They used to survive onto the entry page and fill the top of a phone screen
    with controls that did nothing.
  */
  const listVisible = useIsSplitView() || !selected;

  return (
    <div className="flex flex-col gap-4">
      {/*
        The queue first, everything second. The "Low Value" tab is gone for the
        same reason it left the Calls page: it is a bucket nobody deliberately
        opens. Triage still runs, still keeps low-value entries out of
        Actionable, and still labels them — All is where they are read.
      */}
      {listVisible && (
        <ListTabs
          label="Form entry classification"
          active={triage}
          onChange={(next) => setTriage(next as FormTriageView)}
          tabs={[
            { id: 'actionable', label: 'Actionable' },
            { id: 'all', label: 'All' },
          ]}
        />
      )}

      {!selected && (
        <BulkActionBar count={selection.selectedIds.length} busy={bulk.isPending} onClear={selection.clear}>
          <Button size="sm" disabled={bulk.isPending} onClick={() => bulk.mutate('create_leads')}>
            <Plus className="h-3.5 w-3.5" aria-hidden /> Create Leads with AI
          </Button>
          <Button size="sm" variant="outline" disabled={bulk.isPending} onClick={() => bulk.mutate('dismiss')}>
            <Trash2 className="h-3.5 w-3.5" aria-hidden /> Dismiss
          </Button>
        </BulkActionBar>
      )}
      {bulk.error && (
        <p role="alert" className="text-sm text-destructive">
          {bulk.error instanceof Error ? bulk.error.message : 'The bulk action failed.'}
        </p>
      )}

      {listVisible && (
        <ListToolbar
          search={(
            <SearchField
              value={query}
              onChange={setQuery}
              placeholder="Search name, email, phone, or message…"
              label="Search form entries"
            />
          )}
          filters={(
            <>
              <FilterSelect
                value={recordView}
                onChange={setRecordView}
                label="Form entry view"
                options={[
                  { value: 'default', label: 'Active entries' },
                  { value: 'all', label: 'All entries' },
                  { value: 'archived', label: 'Archived' },
                ]}
              />
              {/*
                Five options in the vocabulary of the lifecycle, not of the
                retired disposition list. "Spam" and "Not qualified" were two of
                eight ways to say Dismissed, and offering them as top-level
                filters asked the reader to remember which word a colleague had
                picked months ago. The stored dispositions are untouched — this
                is what the filter calls them.
              */}
              <FilterSelect
                value={status}
                onChange={setStatus}
                label="Filter by state"
                options={[
                  { value: 'all', label: 'All entries' },
                  { value: 'needs_review', label: 'Needs a decision' },
                  { value: 'lead_created', label: 'Lead created' },
                  { value: 'linked_to_existing_lead', label: 'Linked to lead' },
                ]}
              />
            </>
          )}
          dates={<DateRangeFilter value={dates} onChange={setDates} />}
          count={`${total} ${total === 1 ? 'entry' : 'entries'}`}
          viewToggle={!selected && <CollectionViewToggle view={view} onChange={setView} />}
        />
      )}

      {submissions.isLoading ? (
        <LoadingState />
      ) : submissions.isError ? (
        <ErrorState error={submissions.error} />
      ) : items.length === 0 ? (
        <EmptyState label="No form submissions yet." />
      ) : (
        <WorkspaceSplitView
          detail={selected ? (
            <RecordDetailShell
              recordType="form_entry"
              onClose={() => setSelected(null)}
              onPrevious={index > 0 ? () => setSelected(items[index - 1]!) : undefined}
              onNext={index >= 0 && index < items.length - 1 ? () => setSelected(items[index + 1]!) : undefined}
            >
              <FormSubmissionDetail
                submission={selectedItem ?? selected}
                actions={selectedIntakeState.lead ? (
                  <IntakeLeadHeaderAction lead={selectedIntakeState.lead} />
                ) : undefined}
                contact={(
                  <LinkedContactRecords
                    contacts={selectedIntakeState.contacts}
                    loading={selectedIntakeState.contactsLoading}
                  />
                )}
                overviewBanner={(
                  <IntakeRecordStatusBanner
                    sourceType="form_submission"
                    state={selectedIntakeState}
                    hideResolvedLead
                  />
                )}
                actionCenter={selectedItem && (
                  <IntakeActionCenter
                    recordId={selectedItem.id}
                    sourceType="form_submission"
                    state={selectedIntakeState}
                    onAddNote={async (body) => { await formsApi.addNote(selectedItem.id, body); await refreshSelected(); }}
                  />
                )}
                activity={(
                  <RecordActivity entries={formEntryAnalysis(selectedItem ?? selected)}>
                    {selectedItem && (
                      <SharedRecordManagement
                        notes={selectedItem.notes}
                        archived={selectedItem.archived}
                        // Read-only: the Action Center on Overview owns note entry.
                        notesReadOnly
                        onAddNote={async (body) => { await formsApi.addNote(selectedItem.id, body); await refreshSelected(); }}
                        onArchive={async () => { await formsApi.archive(selectedItem.id, true); await refreshSelected(); }}
                        onRestore={async () => { await formsApi.archive(selectedItem.id, false); await refreshSelected(); }}
                      />
                    )}
                  </RecordActivity>
                )}
              />
            </RecordDetailShell>
          ) : undefined}
        >
          {selected ? (
            <CompactRecordList title={`${submissions.data?.total ?? items.length} submissions`}>
              {items.map((item) => (
                <FormSubmissionListItem
                  key={item.id}
                  submission={item}
                  selected={item.id === selected.id}
                  onSelect={() => setSelected(item)}
                />
              ))}
            </CompactRecordList>
          ) : view === 'cards' ? (
            <div className="flex flex-col gap-4">
              <RecordCardGrid>
                {items.map((item) => (
                  <div key={item.id} className="relative">
                    {actionableIds.includes(item.id) && (
                      <span className="absolute left-3 top-3 z-10 rounded bg-background/90 p-1">
                        <SelectionCheckbox
                          checked={selection.selected.has(item.id)}
                          onChange={() => selection.toggle(item.id)}
                          label={`Select ${item.name ?? 'form entry'}`}
                        />
                      </span>
                    )}
                    <FormSubmissionCard
                      submission={item}
                      onSelect={() => setSelected(item)}
                      badges={
                        <IntakeStateBadge
                          lifecycle={item.lifecycle}
                          resolutionLabel={item.resolutionLabel}
                        />
                      }
                    />
                  </div>
                ))}
              </RecordCardGrid>
              {pagination}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
            <RecordTable
              columns={[
                {
                  key: 'select',
                  header: (
                    <SelectionCheckbox
                      checked={selection.allSelected}
                      onChange={selection.toggleAll}
                      label="Select all actionable Form Entries"
                    />
                  ),
                  className: 'w-10',
                },
                { key: 'name', header: 'Name' },
                { key: 'submitted', header: 'Submitted' },
                { key: 'form', header: 'Form', hideOnMobile: true },
                { key: 'classification', header: 'Classification' },
                { key: 'email', header: 'Email', hideOnMobile: true },
                { key: 'phone', header: 'Phone', hideOnMobile: true },
                { key: 'state', header: 'State', align: 'right' },
              ]}
            >
              {items.map((item) => (
                <RecordTableRow
                  key={item.id}
                  selected={selection.selected.has(item.id)}
                  onOpen={() => setSelected(item)}
                >
                  <RecordTableCell>
                    {actionableIds.includes(item.id) && (
                      <SelectionCheckbox
                        checked={selection.selected.has(item.id)}
                        onChange={() => selection.toggle(item.id)}
                        label={`Select ${item.name ?? 'form entry'}`}
                      />
                    )}
                  </RecordTableCell>
                  <RecordTablePrimaryCell
                    title={item.name ?? 'No name submitted'}
                    subtitle={item.message ?? item.summary}
                  />
                  <RecordTableCell>{formatDateTime(item.submittedAt)}</RecordTableCell>
                  <RecordTableCell hideOnMobile>{item.formName}</RecordTableCell>
                  <RecordTableCell><FormClassificationPill submission={item} /></RecordTableCell>
                  <RecordTableCell hideOnMobile>
                    {item.email && (
                      <a
                        className="text-primary hover:underline"
                        href={`mailto:${item.email}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {item.email}
                      </a>
                    )}
                  </RecordTableCell>
                  <RecordTableCell hideOnMobile>
                    {item.phone && (
                      <a
                        className="text-primary hover:underline"
                        href={`tel:${item.phone.replace(/[^\d+]/g, '')}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {item.phone}
                      </a>
                    )}
                  </RecordTableCell>
                  <RecordTableCell align="right">
                    <IntakeStateBadge
                      lifecycle={item.lifecycle}
                      resolutionLabel={item.resolutionLabel}
                    />
                  </RecordTableCell>
                </RecordTableRow>
              ))}
            </RecordTable>
            {pagination}
            </div>
          )}
        </WorkspaceSplitView>
      )}
    </div>
  );
}

/**
 * What ran on this entry and why it decided what it did.
 *
 * This is the Classification block that used to open the Overview, moved to
 * where a reader goes when they disagree with a badge. Naming the Switch
 * matters: a classification that is consistently wrong is a configuration
 * problem, and this is the only place that says which configuration.
 */
function formEntryAnalysis(submission: CascadeSubmission): RecordAnalysisEntry[] {
  if (!submission.classification) return [];
  return [
    {
      id: `triage:${submission.id}`,
      source: 'Intelligent Form Triage',
      result: submission.classificationLabel ?? submission.classification,
      explanation: submission.classificationExplanation,
      occurredAt: submission.classifiedAt,
    },
  ];
}

/* ------------------------------------------------------------------------ forms */

export function FormsView() {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<CascadeForm | null>(null);
  const [view, setView] = useCollectionView('cascade:records:forms:view');

  const forms = useQuery({
    queryKey: ['cascade-forms', query],
    queryFn: () => formsApi.forms({ q: query || undefined }),
  });

  const items = forms.data?.items ?? [];
  const index = selected ? items.findIndex((item) => item.id === selected.id) : -1;
  const listVisible = useIsSplitView() || !selected;

  return (
    <div className="flex flex-col gap-4">
      {listVisible && (
        <ListToolbar
          search={(
            <SearchField
              value={query}
              onChange={setQuery}
              placeholder="Search form or site…"
              label="Search forms"
            />
          )}
          count={`${items.length} ${items.length === 1 ? 'form' : 'forms'}`}
          viewToggle={!selected && <CollectionViewToggle view={view} onChange={setView} />}
        />
      )}

      {forms.isLoading ? (
        <LoadingState />
      ) : forms.isError ? (
        <ErrorState error={forms.error} />
      ) : items.length === 0 ? (
        <EmptyState label="No forms have submitted yet. A form appears here the first time it sends an entry." />
      ) : (
        <WorkspaceSplitView
          detail={selected ? (
            <FormDetailPane
              form={selected}
              onClose={() => setSelected(null)}
              onPrevious={index > 0 ? () => setSelected(items[index - 1]!) : undefined}
              onNext={index >= 0 && index < items.length - 1 ? () => setSelected(items[index + 1]!) : undefined}
            />
          ) : undefined}
        >
          {selected ? (
            <CompactRecordList title={`${items.length} forms`}>
              {items.map((item) => (
                <CompactRecordListItem
                  key={item.id}
                  selected={item.id === selected.id}
                  title={item.name}
                  subtitle={item.siteName ?? item.siteUrl}
                  meta={<RecordPill tone="muted">{item.submissionCount} submissions</RecordPill>}
                  onSelect={() => setSelected(item)}
                />
              ))}
            </CompactRecordList>
          ) : view === 'cards' ? (
            <RecordCardGrid>
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelected(item)}
                  className="w-full overflow-hidden rounded-lg border bg-card text-left transition-colors hover:border-primary/40 hover:bg-accent/30"
                >
                  <RecordTypeStrip type="form" />
                  <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">{item.name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {item.siteName ?? item.siteUrl}
                      </div>
                    </div>
                    {!item.isActive && <Badge variant="muted">Inactive</Badge>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                    <span>{item.submissionCount} submissions</span>
                    <span>rev {item.revision}</span>
                    {item.lastSubmissionAt && <span>last {formatDateTime(item.lastSubmissionAt)}</span>}
                  </div>
                  </div>
                </button>
              ))}
            </RecordCardGrid>
          ) : (
            <RecordTable
              columns={[
                { key: 'form', header: 'Form' },
                { key: 'site', header: 'Site' },
                { key: 'active', header: 'Active', hideOnMobile: true },
                { key: 'submissions', header: 'Submissions', align: 'right' },
                { key: 'last', header: 'Last submission', hideOnMobile: true },
                { key: 'revision', header: 'Revision', align: 'right', hideOnMobile: true },
                { key: 'schema', header: 'Schema changed', hideOnMobile: true },
              ]}
            >
              {items.map((item) => (
                <RecordTableRow key={item.id} onOpen={() => setSelected(item)}>
                  <RecordTablePrimaryCell title={item.name} />
                  <RecordTableCell>{item.siteName ?? item.siteUrl}</RecordTableCell>
                  <RecordTableCell hideOnMobile>
                    <Badge variant={item.isActive ? 'success' : 'muted'}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </RecordTableCell>
                  <RecordTableCell align="right">{item.submissionCount}</RecordTableCell>
                  <RecordTableCell hideOnMobile>{formatDateTime(item.lastSubmissionAt)}</RecordTableCell>
                  <RecordTableCell align="right" hideOnMobile>{item.revision}</RecordTableCell>
                  <RecordTableCell hideOnMobile>{formatDateTime(item.schemaChangedAt)}</RecordTableCell>
                </RecordTableRow>
              ))}
            </RecordTable>
          )}
        </WorkspaceSplitView>
      )}
    </div>
  );
}

function FormDetailPane({
  form,
  onClose,
  onPrevious,
  onNext,
}: {
  form: CascadeForm;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
}) {
  const detail = useQuery({
    queryKey: ['cascade-form-detail', form.id],
    queryFn: () => formsApi.form(form.id),
  });
  const item = detail.data?.form ?? form;
  const submissions = detail.data?.submissions ?? [];

  return (
    <RecordDetailShell
      recordType="form"
      onClose={onClose}
      onPrevious={onPrevious}
      onNext={onNext}
    >
      <div className="border-b border-border px-4 py-3">
        <h2 className="break-words text-[15px] font-semibold leading-tight text-foreground">
          {item.name}
        </h2>
      </div>
      {detail.isError && <ErrorState error={detail.error} />}
      <div className="space-y-5 p-4">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
          <Meta label="Site" value={item.siteName ?? item.siteUrl} />
          <Meta label="Active" value={item.isActive ? 'Yes' : 'No'} />
          <Meta label="Submissions" value={String(item.submissionCount)} />
          <Meta label="Last submission" value={formatDateTime(item.lastSubmissionAt)} />
          <Meta label="Revision" value={String(item.revision)} />
          <Meta label="Schema changed" value={formatDateTime(item.schemaChangedAt)} />
          <Meta label="Source URL" value={item.pageUrl ?? item.siteUrl} />
          <Meta label="Form ID" value={item.externalFormId} />
        </dl>

        <section>
          <h3 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <FileText className="h-3.5 w-3.5" />
            Current fields ({item.fields.length})
          </h3>
          {item.fields.length === 0 ? (
            <p className="mt-1 text-sm text-muted-foreground">No field structure recorded yet.</p>
          ) : (
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {item.fields.map((field) => (
                <li key={field.label} className="rounded bg-muted px-2 py-0.5 text-xs">
                  {field.label}
                  <span className="ml-1 text-muted-foreground">{field.type}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h3 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <Inbox className="h-3.5 w-3.5" />
            Recent submissions
          </h3>
          {submissions.length === 0 ? (
            <p className="mt-1 text-sm text-muted-foreground">No submissions yet.</p>
          ) : (
            <div className="mt-2 grid gap-2">
              {submissions.slice(0, 10).map((submission) => (
                <FormSubmissionCard key={submission.id} submission={submission} />
              ))}
            </div>
          )}
        </section>
      </div>
    </RecordDetailShell>
  );
}

function Meta({ label, value }: { label: string; value?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate text-sm">{value ?? '—'}</dd>
    </div>
  );
}

/* -------------------------------------------------------------------- feed tabs */

type FormsTab = 'entries' | 'forms';

/**
 * The Forms feed. Entries lead because that is the queue a reviewer works;
 * the forms themselves are reference data behind a tab, matching how Voice
 * Agents leads with calls.
 */
export function FormsFeedView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: FormsTab = searchParams.get('tab') === 'forms' ? 'forms' : 'entries';

  /*
    Page identity is registered with the AppShell rather than rendered here.
    This page used to print its own title and subtitle above the shell's, which
    is how every feed ended up saying its own name twice.
  */
  usePageHeader(
    {
      title: 'Form Entries',
      subtitle: 'Website submissions, and the forms that produced them.',
    },
    [],
  );

  const select = (next: FormsTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'forms') params.set('tab', 'forms');
    else params.delete('tab');
    setSearchParams(params, { replace: true });
  };

  /*
    The feed switch belongs to the list, not to a record. On a phone an open
    entry is the whole screen, and leaving "Form Entries | Forms" above it
    offers a jump to a different feed as the first thing on a detail page.
  */
  const entryOpen = Boolean(searchParams.get('entry'));
  const feedTabsVisible = useIsSplitView() || !entryOpen;

  return (
    <div className="flex flex-col gap-4">
      {feedTabsVisible && (
        <ListTabs
          label="Form feeds"
          active={tab}
          onChange={(next) => select(next as FormsTab)}
          tabs={[
            { id: 'entries', label: 'Form Entries', icon: <ClipboardList className="h-4 w-4" /> },
            { id: 'forms', label: 'Forms', icon: <FileText className="h-4 w-4" /> },
          ]}
        />
      )}
      {tab === 'entries' ? <FormSubmissionsView /> : <FormsView />}
    </div>
  );
}
